import { useEffect, useState } from 'react';
import { QueryParamDrivenDrawerPage } from '../../components/CareMapDrawerPage';
import { IQueryParamDrivenDrawerContentProvider } from '../../components/query-param-driver-drawer/IQueryParamDrivenDrawerContentProvider';
import { QueryParamDrivenDrawerHelper } from '../../components/query-param-driver-drawer/QueryParamDrivenDrawerHelper';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { IonButton } from '@ionic/react';
import { NEW_MEDICATION_ID } from '../../caremap/ICareMap';
import { MedicationEditor } from './MedicationEditor';
import { useSubscription } from '@meraki-internal/state';
import { CareMapState } from '../../caremap/CareMapState';
import { MedicationFormState } from './MedicationFormState';
import { AppPrincipal, isAnonymousViewer } from '../../auth/AppPrincipal';
import { Menu } from '../../components/Menu';
import { DeleteIcon } from '../../theme/Icons';
import { MixPanelEventEmitter } from '../../metrics/MixPanelEventEmitter';

interface IMedicationEditorDrawerProps {
    id: string;
};

export class MedicationEditorDrawer implements IQueryParamDrivenDrawerContentProvider<IMedicationEditorDrawerProps> {
    static inject = () => [QueryParamDrivenDrawerHelper];
    constructor(private drawerHelper: QueryParamDrivenDrawerHelper<IMedicationEditorDrawerProps>){}

    mode = 'edit-medication';

    Component = EditMedicationDrawerView;

    open = (props: IMedicationEditorDrawerProps) => {
        this.drawerHelper.open({ mode: this.mode, ...props });
    };

    buildQueryParams = (props: IMedicationEditorDrawerProps) => {
        return this.drawerHelper.buildQueryParams({ mode: this.mode, id: props.id });
    };
}

const EditMedicationDrawerView: React.FC<IMedicationEditorDrawerProps> = ({ id }) => {
    const mixpanel = useInstance(MixPanelEventEmitter);

    const [saving, setSaving] = useState(false);
    const drawerHelper = useInstance(QueryParamDrivenDrawerHelper);

    const careMapState = useInstance(CareMapState);
    useSubscription(() => careMapState);

    const medicationForm = useInstance(MedicationFormState);
    useSubscription(() => medicationForm);

    const isNew = id === NEW_MEDICATION_ID;
    const medication = medicationForm.state.medication;

    const isViewer = !careMapState.hasPermissionTo('put');
    const isAnonymous = isAnonymousViewer(useInstance(AppPrincipal));
    const canAddEdit = !isViewer && !isAnonymous;

    useEffect(() => {
        if (!isNew && medication?.id) {
            mixpanel.track('Medication Viewed', () => ({
                'Name': medication?.name,
                'Has Instructions': medication?.instructions ? true : false,
                'Has Dose': medication?.dose ? true : false,
                'Has RX #': medication?.rxNumber ? true : false,
                'Has Refills': medication?.refills ? true : false,
                'Has Expiration Date': medication?.expiration ? true : false,
                'Has Notes': medication?.notes ? true : false
            }));
        }
    }, [mixpanel, isNew, medication]);

    const [isValid, setIsValid] = useState<boolean>(false);

    if (!careMapState.getActiveCaremap()) {
        return null;
    }

    const deleteMedication = async () => {
        setSaving(true);
        medicationForm.delete();
        drawerHelper.close();
    };

    const save = async () => {
        if (medication) {
            setSaving(true);
            medicationForm.updateCurrentPatient();
            drawerHelper.close();
        }
    };

    const cancel = () => {
        medicationForm.clear();
        drawerHelper.close();
    };

    const buttons = canAddEdit ? <>
        <IonButton data-type="save-button" disabled={!isValid || saving} onClick={save}>Save</IonButton>
        <IonButton data-type="cancel-button" fill="outline" onClick={cancel}>Cancel</IonButton>
    </> : <>
        <div data-type="cannot-save" />
    </>;

    return (
        <QueryParamDrivenDrawerPage
            hasBack
            buttons={buttons}
            onClose={cancel}
            data-type="medication-editor"
            title={`${!canAddEdit ? 'View' : isNew ? 'Add' : 'Edit'} Medication`}
        >
            <>
                {(canAddEdit && !isNew) ? (
                    <div style={{marginTop: -8, display: 'flex', justifyContent: 'end'}}>
                        <Menu data-id="edit-medication-menu" actions={[{
                            Icon: DeleteIcon,
                            role: 'destructive',
                            text: `Delete Medication`,
                            handler: deleteMedication,
                            'data-type': 'delete-button',
                            disabled: saving
                        }]} />
                    </div>
                ) : <div data-type="no-menu" />}

                <MedicationEditor
                    id={id}
                    readOnly={!canAddEdit}
                    reportValidity={(valid) => setIsValid(valid)}
                />
            </>
        </QueryParamDrivenDrawerPage>
    );
};
